import { t } from 'i18next';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { toast } from '@/lib/v2/components';

import {
  CustomField,
  IntegrationStepsData,
} from '@/src/infrastructure/interfaces/IIntegrations.interface';
import { useIntegrationService } from '@/src/infrastructure/Protocol/Integrations/useIntegrationService';
import { atomCreateOrEditIntegrationForm } from '@/src/modules/IntegrationsModule/atoms/integrations';
import { INTEGRATIONS_PATHS, STEP_THREE_OPTIONS } from '@/src/modules/IntegrationsModule/constants';
import { OPTION_TWO } from '@/src/modules/IntegrationsModule/screens/CreateOrEditIntegrationScreen/components/Steps/StepOne';

export const useSaveDataIntegration = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = useIntegrationService();
  const stepsData = useAtomValue(atomCreateOrEditIntegrationForm);
  const [isLoading, setIsLoading] = useState(false);

  const goToIntegrationsScreen = useCallback(() => {
    navigate(`/v2/${INTEGRATIONS_PATHS.MODULE}`);
  }, [navigate]);

  const handleSaveData = useCallback(async () => {
    setIsLoading(true);

    const customFields: CustomField[] = stepsData.stepTwo
      .filter((field) => field.value !== 'email' && field.isChecked)
      .map((field) => ({
        id: field.id,
        name: field.name,
        type: field.type,
      }));

    let addGroupIds = undefined;
    if (stepsData.stepThree) {
      addGroupIds =
        stepsData.stepThree.value === STEP_THREE_OPTIONS.NOTHING
          ? []
          : stepsData.stepThreeGroups.map((group) => group.value as number) || [];
    }

    const transformedData: IntegrationStepsData = {
      addBulk: stepsData.stepOne?.value === OPTION_TWO,
      customFields,
      addGroupIds,
      apiKeyId: stepsData.stepFour.id === '' ? undefined : (stepsData.stepFour.id as string),
      activate: stepsData.stepFourSwitch,
    };

    const response = await service.saveIntegration(Number(id), transformedData);
    setIsLoading(false);
    if (response.success) {
      toast({
        title: t('useSaveDataIntegration.success'),
        body: t('useSaveDataIntegration.body.success'),
      });
      goToIntegrationsScreen();
    }
  }, [
    goToIntegrationsScreen,
    id,
    service,
    stepsData.stepFour.id,
    stepsData.stepFourSwitch,
    stepsData.stepOne?.value,
    stepsData.stepThree,
    stepsData.stepThreeGroups,
    stepsData.stepTwo,
  ]);

  return { isLoading, handleSaveData, goToIntegrationsScreen };
};
